import {
  createContext,
  useContext,
  PropsWithChildren,
  useEffect,
  useState,
} from "react";

import { useAuth0 } from "@auth0/auth0-react";
import axios, { AxiosInstance } from "axios";

import FullScreenInit from "components/FullScreenInit";

import CONFIG from "utils/config";
import { setupPostHogApiInterceptor } from "utils/posthogApiInterceptor";
import posthog from "posthog-js";

type AuthContextStore = {
  authApi: AxiosInstance;
};

const axiosInstance = axios.create({
  baseURL: `${CONFIG.API_URL}/v2`,
});

setupPostHogApiInterceptor(axiosInstance);

const AuthContext = createContext<AuthContextStore>({ authApi: axiosInstance });

// eslint-disable-next-line @typescript-eslint/ban-types
type AuthProviderProps = PropsWithChildren<{
  allowPublic?: boolean;
}>;

const AuthProvider = ({ children, allowPublic = false }: AuthProviderProps) => {
  const [isAxiosAuth, setIsAxiosAuth] = useState(false);
  const {
    user,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
  } = useAuth0();

  useEffect(() => {
    if (isLoading) return;
    let interceptor: number | null = null;
    if (isAuthenticated) {
      // faro?.api?.setUser(user);
      posthog.identify(user!.sub!, {
        email: user!.email,
        name: user!.name,
      });

      // This is for the old API.
      // TODO: Remove once the old api has been deprecated
      window.getAccessToken = getAccessTokenSilently;
      window.getIdToken = getIdTokenClaims;
      interceptor = axiosInstance.interceptors.request.use(async (config) => {
        const token = await getAccessTokenSilently();

        if (config && config.headers) {
          config.headers["Content-Type"] = "application/json";
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      });
      setIsAxiosAuth(true);
      return () => {
        if (interceptor) {
          axiosInstance.interceptors.request.eject(interceptor);
        }
        setIsAxiosAuth(false);
      };
    } else if (!isLoading) {
      posthog.reset();
      if (!allowPublic) {
        loginWithRedirect();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  if (isLoading || ((!isAuthenticated || !isAxiosAuth) && !allowPublic)) {
    return <FullScreenInit loading={isLoading || !isAxiosAuth} />;
  }

  return (
    <AuthContext.Provider value={{ authApi: axiosInstance }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
